import type { backendOld } from "~/api";
import { MODEL_TO_AVAILABLE_THINKING_LEVELS, MODEL_TO_DEFAULT_THINKING_LEVEL } from "~/constants/model";
import { isDefined } from "~/shared/common";
import { AiModel, ChatMessageKind } from "~/shared/enums";
import type {
  Chat,
  ChatCreate,
  ChatUpdate,
  DraftMessage,
  ExtraItem,
  Skill,
  TransactionResponse,
  UserTextMessage,
} from "~/shared/types";
import type { PartialWithDuid } from "~/shared/typeUtils";
import { filterInPlace, isLexicalStateEmpty, makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { chatComparator, scheduledRunComparator } from "~/utils/comparator";
import { isAgentDisabled } from "~/utils/user";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { makeDraftMessageCreate } from "./chatMessage";

const CHATS_PAGINATION_KEY = "chats";
const REUSABLE_EMPTY_CHAT_SEARCH_LIMIT = 10;

type ChatFilters = {
  duids?: string[];
  rootTaskDuid?: string;
  search?: string;
  sourceSkillDuid?: string;
  sourceAgentScheduleDuid?: string;
  targetUserDuid?: string;
};

const getChatsPaginationExtra = (filters: ChatFilters) =>
  JSON.stringify([
    filters.duids ?? null,
    filters.rootTaskDuid ?? null,
    filters.search ?? null,
    filters.sourceSkillDuid ?? null,
    filters.sourceAgentScheduleDuid ?? null,
    filters.targetUserDuid ?? null,
  ]);

const getChatComparator = (filters: ChatFilters) =>
  filters.sourceSkillDuid || filters.sourceAgentScheduleDuid ? scheduledRunComparator : chatComparator;

const doesChatMatchFilters = (chat: Chat, filters: ChatFilters) => {
  if (filters.duids && !filters.duids.includes(chat.duid)) {
    return false;
  }
  if ((filters.rootTaskDuid ?? null) !== (chat.rootTaskDuid ?? null)) {
    return false;
  }
  if (filters.search && !chat.title.toLowerCase().includes(filters.search.toLowerCase())) {
    return false;
  }
  if ((filters.sourceSkillDuid ?? null) !== (chat.sourceSkillDuid ?? null)) {
    return false;
  }
  if ((filters.sourceAgentScheduleDuid ?? null) !== (chat.sourceAgentScheduleDuid ?? null)) {
    return false;
  }
  if (filters.targetUserDuid && chat.targetUserDuid !== filters.targetUserDuid) {
    return false;
  }
  return true;
};

const getFirstReusableEmptyChat = async (
  dataStore: DataStore,
  candidates: Chat[],
  index = 0
): Promise<Chat | undefined> => {
  const candidate = candidates[index];
  if (!candidate) {
    return undefined;
  }
  if (!dataStore.areChatMessagesLoaded(candidate.duid)) {
    await dataStore.loadChatMessagesForChat(candidate.duid);
  }
  if (dataStore.isChatEmpty(candidate, false)) {
    return candidate;
  }
  return getFirstReusableEmptyChat(dataStore, candidates, index + 1);
};

export type State = {
  _duidsToChats: Map<string, Chat>;
  _chatDuidToDraftAccess: Map<string, ChatUpdate>;
  _chatDuidsDeleted: Set<string>;
};

export const state: State = {
  _duidsToChats: new Map(),
  _chatDuidToDraftAccess: new Map(),
  _chatDuidsDeleted: new Set(),
};

export type Getters = {
  getChatByDuid: (duid: string) => Promise<Chat | undefined>;
  getChatByDuidIfLoaded: (duid: string) => Chat | undefined;
  getDraftAccessByChatDuid: (chatDuid: string) => ChatUpdate | undefined;
  getLoadedChatsOrdered: (filters?: ChatFilters) => Chat[];
  getLoadedChatCount: (filters?: ChatFilters) => number;
  isChatEmpty: (chat: Chat, includeDraft?: boolean) => boolean;
  isChatAgentWorking: (chat: Chat) => boolean;
  areChatsLoaded: (filters?: ChatFilters) => boolean;
};

export type Actions = {
  loadChats: (...args: Parameters<typeof backendOld.entityList.chat>) => ReturnType<typeof backendOld.entityList.chat>;
  loadPaginatedChats: (filters?: ChatFilters, limit?: number) => Promise<number>;
  resetPaginationStateForChats: (filters?: ChatFilters) => void;
  createChat: (
    chatCreate: ChatCreate,
    draft: DraftMessage,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Chat | undefined>;
  updateChat: (
    update: ChatUpdate & { read?: boolean },
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Chat | undefined>;
  updateChatAccessors: (
    chatDuid: string,
    accessorDuids: string[],
    add?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<Chat | undefined>;
  deleteChat: (chat: Chat, options?: { awaitBackend?: boolean }) => Promise<void>;
  sendSkillInNewChat: (skill: Skill, extras: ExtraItem[]) => Promise<void>;
  findReusableEmptyChatForAgent: (
    agentDuid: string,
    rootTaskDuid?: string,
    excludedChatDuid?: string
  ) => Promise<Chat | undefined>;
  $createChatNoBackend: (chatCreate: ChatCreate, draft: DraftMessage) => Chat | undefined;
  $createChat: (chat: Chat) => void;
  $updateChat: (chat: PartialWithDuid<Chat>) => void;
  $saveChat: (chat: PartialWithDuid<Chat>) => void;
  $deleteChat: (chat: Chat) => void;
  $setChatDraftAccess: (chatDuid: string, update: ChatUpdate) => void;
  $deleteChatDraftAccess: (chatDuid: string) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getChatByDuid() {
    return async (duid) => {
      const chat = this.getChatByDuidIfLoaded(duid);
      if (chat) {
        return chat;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      const { data } = await this.loadChats({ duids: [duid] }, 0, 1);
      const chatFetched = data.results[0];
      if (!chatFetched) {
        this.$blacklistEntityDuid("chats", duid);
        return undefined;
      }

      return chatFetched;
    };
  },
  getChatByDuidIfLoaded() {
    return (duid) => this._duidsToChats.get(duid);
  },
  getDraftAccessByChatDuid() {
    return (chatDuid) => this._chatDuidToDraftAccess.get(chatDuid);
  },
  getLoadedChatsOrdered() {
    return (filters = {}) => {
      const paginationState = this.$getPaginationState(CHATS_PAGINATION_KEY, getChatsPaginationExtra(filters));
      if (!paginationState) {
        return [];
      }
      return [...paginationState.duids.values()]
        .map((duid) => this._duidsToChats.get(duid))
        .filter(isDefined)
        .filter((chat) => !chat.targetUserDuid || !!this.getUserByDuid(chat.targetUserDuid))
        .sort(getChatComparator(filters));
    };
  },
  getLoadedChatCount() {
    return (filters = {}) =>
      this.$getPaginationState(CHATS_PAGINATION_KEY, getChatsPaginationExtra(filters))?.duids.size ?? 0;
  },
  isChatEmpty() {
    return (chat, includeDraft = true) => {
      if (!this.areChatMessagesLoaded(chat.duid)) {
        return false;
      }
      if (this.getChatMessagesByChatDuidOrdered(chat.duid).length !== 0) {
        return false;
      }
      if (!includeDraft) {
        return true;
      }
      const draft = this.getDraftMessageByChatDuid(chat.duid);
      return !draft || isLexicalStateEmpty(draft.content);
    };
  },
  isChatAgentWorking() {
    return (chat) => {
      const agent = chat.targetUserDuid ? this.getUserByDuid(chat.targetUserDuid) : undefined;
      return agent?.agent?.activeSessions?.some((e) => e.chatDuid === chat.duid) ?? false;
    };
  },
  areChatsLoaded() {
    return (filters = {}) =>
      this.$getPaginationState(CHATS_PAGINATION_KEY, getChatsPaginationExtra(filters)) !== undefined;
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadChats(...args) {
    const response = await this.$backendOld.entityList.chat(...args);
    response.data.results.forEach((chat) => this.$createChat(chat));
    return response;
  },
  async loadPaginatedChats(filtersMaybe, limit) {
    const filters = filtersMaybe ?? {};
    const extra = getChatsPaginationExtra(filters);
    const validator = (entity: { duid: string }) => doesChatMatchFilters(entity as Chat, filters);
    const paginationState = this.$getPaginationState(CHATS_PAGINATION_KEY, extra);
    if (paginationState) {
      paginationState.validator = validator;
    }

    const { data } = await this.loadChats(filters, paginationState?.duids.size ?? 0, limit);
    const currentPaginationState = this.$getPaginationState(CHATS_PAGINATION_KEY, extra);
    if (currentPaginationState) {
      currentPaginationState.validator = validator;
    } else {
      this.$createPaginationState(CHATS_PAGINATION_KEY, extra, getChatComparator(filters), validator);
    }
    this.$addEntitiesToPaginationState(CHATS_PAGINATION_KEY, extra, data.results, data.count);

    return data.count;
  },
  resetPaginationStateForChats(filtersMaybe) {
    const filters = filtersMaybe ?? {};
    this.$clearPaginationState(CHATS_PAGINATION_KEY, getChatsPaginationExtra(filters));
  },
  async createChat(chatCreate, draft, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const chat = this.$createChatNoBackend(chatCreate, draft);
    if (!chat) {
      return undefined;
    }

    if (noBackend) {
      return chat;
    }

    const backendAction = this.$backend.chat.createWithDraft(
      chatCreate,
      makeDraftMessageCreate(draft, chatCreate.duid)
    );
    if (awaitBackend) {
      await backendAction;
    }
    return chat;
  },
  async updateChat(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const chat = this.getChatByDuidIfLoaded(update.duid);
    if (!chat) {
      return undefined;
    }

    const { read, ...chatUpdate } = update;
    Object.assign(chat, update);

    const backendActions: Promise<unknown>[] = [];
    if (!noBackend) {
      if (Object.entries(chatUpdate).some(([key, value]) => key !== "duid" && value !== undefined)) {
        backendActions.push(this.$backend.chat.update(chatUpdate));
      }
      if (read !== undefined) {
        backendActions.push(this.$backendOld.chat.setRead(chat.duid, read));
      }
    }
    if (awaitBackend) {
      await Promise.all(backendActions);
    }
    return chat;
  },
  async updateChatAccessors(duid, accessorDuids, add, options = {}) {
    const { awaitBackend = false } = options;
    const chat = this.getChatByDuidIfLoaded(duid);
    if (!chat) {
      return undefined;
    }

    let backendAction: Promise<TransactionResponse>;

    const payload = {
      duid: chat.duid,
      accessibleByUserDuids: accessorDuids,
    };

    if (add) {
      chat.accessibleByUserDuids.push(...accessorDuids);
      backendAction = this.$backend.chat.updateListAdd(payload);
    } else {
      filterInPlace(chat.accessibleByUserDuids, (e) => !accessorDuids.includes(e));
      backendAction = this.$backend.chat.updateListRemove(payload);
    }

    if (awaitBackend) {
      await backendAction;
    }

    return chat;
  },
  async deleteChat(chat, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteChat(chat);

    this._chatDuidsDeleted.add(chat.duid);
    const backendAction = this.$backend.chat.delete(chat.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  async sendSkillInNewChat(skill, extras) {
    const agent = this.dartAi;
    if (isAgentDisabled(agent)) {
      this.$useUserStore().setAiChatPanelOpen(true);
      return;
    }
    const model = skill.aiModel ?? AiModel.AUTO;
    const skillThinkingLevel = skill.aiThinkingLevel;
    const thinkingLevel = (MODEL_TO_AVAILABLE_THINKING_LEVELS.get(model) ?? []).includes(skillThinkingLevel)
      ? skillThinkingLevel
      : MODEL_TO_DEFAULT_THINKING_LEVEL[model];
    const now = new Date().toISOString();
    const chatDuid = makeDuid();
    const draft: DraftMessage = {
      duid: makeDuid(),
      chatDuid,
      kind: ChatMessageKind.DRAFT,
      authorDuid: this.$useUserStore().duid,
      content: makeEmptyLexicalState(),
      extras,
      createdAt: now,
      attachmentDuids: skill.attachmentDuids,
    };
    const chat = await this.createChat(
      {
        duid: chatDuid,
        targetUserDuid: agent.duid,
        model,
        thinkingLevel,
        webEnabled: skill.aiWebEnabled,
      },
      draft,
      { awaitBackend: true }
    );
    if (!chat) {
      return;
    }
    const userMessage: UserTextMessage = {
      duid: makeDuid(),
      chatDuid,
      kind: ChatMessageKind.USER_TEXT,
      authorDuid: this.$useUserStore().duid,
      content: skill.prompt,
      extras,
      createdAt: now,
      attachmentDuids: skill.attachmentDuids,
      webEnabled: skill.aiWebEnabled,
    };
    this.addMessageToChatNoBackend(chatDuid, userMessage);
    await this.$backendOld.chat.streamMessage(userMessage, chatDuid, () => undefined);
  },
  async findReusableEmptyChatForAgent(agentDuid, rootTaskDuid, excludedChatDuid) {
    const filters = { rootTaskDuid, targetUserDuid: agentDuid };
    if (!this.areChatsLoaded(filters)) {
      await this.loadPaginatedChats(filters, REUSABLE_EMPTY_CHAT_SEARCH_LIMIT);
    }

    const candidates = this.getLoadedChatsOrdered(filters)
      .filter((item) => !item.pinned && item.duid !== excludedChatDuid)
      .slice(0, REUSABLE_EMPTY_CHAT_SEARCH_LIMIT);
    return getFirstReusableEmptyChat(this, candidates);
  },
  $createChatNoBackend(chatCreate, draft) {
    const now = new Date().toISOString();
    const model = chatCreate.model ?? AiModel.AUTO;
    let { thinkingLevel } = chatCreate;
    if (thinkingLevel === undefined || !(MODEL_TO_AVAILABLE_THINKING_LEVELS.get(model) ?? []).includes(thinkingLevel)) {
      thinkingLevel = MODEL_TO_DEFAULT_THINKING_LEVEL[model];
    }
    const chat: Chat = {
      createdAt: now,
      updatedAt: now,
      title: "",
      userDuid: this.$useUserStore().duid,
      sourceSkillDuid: null,
      sourceAgentScheduleDuid: null,
      webEnabled: chatCreate.webEnabled ?? true,
      extrasAreUserManaged: false,
      isDemo: false,
      pinned: false,
      read: true,
      accessibleByTeam: false,
      accessibleByUserDuids: [],
      working: false,
      ...chatCreate,
      rootTaskDuid: chatCreate.rootTaskDuid ?? null,
      model,
      thinkingLevel,
    };

    this.$createChat(chat);
    this.$createDraftMessageNoBackend(chat, draft, { messagesAreKnownEmpty: true });

    return chat;
  },
  $createChat(chat) {
    const currentChat = this.getChatByDuidIfLoaded(chat.duid);
    if (this._chatDuidsDeleted.has(chat.duid)) {
      return;
    }

    const userDuid = this.$useUserStore().duid;
    const isAccessible =
      chat.userDuid === userDuid ||
      chat.accessibleByTeam ||
      chat.accessibleByUserDuids.includes(userDuid) ||
      (chat.sourceSkillDuid !== null && !!this.getSkillByDuid(chat.sourceSkillDuid)) ||
      chat.sourceAgentScheduleDuid !== null;

    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (!chat.rootTaskDuid && !isAccessible) {
      if (currentChat) {
        this.$deleteChat(chat);
      }
      return;
    }

    if (!currentChat) {
      this._duidsToChats.set(chat.duid, chat);
      this.$unblacklistEntityDuid(chat.duid);
      this.$maybeRemoveInvalidEntityFromPaginationStates(chat);
      this.$addEntitiesToPaginationStates(CHATS_PAGINATION_KEY, [chat]);
      return;
    }

    this.$saveChat(chat);
  },
  $updateChat(chat) {
    this.$saveChat(chat);
  },
  $saveChat(chat) {
    const currentChat = this.getChatByDuidIfLoaded(chat.duid);
    if (!currentChat) {
      return;
    }

    Object.assign(currentChat, chat);
    this.$maybeRemoveInvalidEntityFromPaginationStates(currentChat);
    this.$addEntitiesToPaginationStates(CHATS_PAGINATION_KEY, [currentChat]);
  },
  $deleteChat(chat) {
    this._chatDuidToDraftAccess.delete(chat.duid);
    this.$deleteChatMessagesForChat(chat.duid);
    this._duidsToChats.delete(chat.duid);
    this.$removeEntitiesFromPaginationStates([chat]);

    const appStore = this.$useAppStore();
    const chatPanels = [appStore.chatPanel, appStore.agentChatPanel, appStore.taskChatPanel];
    chatPanels.filter((e) => e?.chat?.duid === chat.duid).forEach((e) => e?.createNewChat());
  },
  $setChatDraftAccess(chatDuid, update) {
    this._chatDuidToDraftAccess.set(chatDuid, update);
  },
  $deleteChatDraftAccess(chatDuid) {
    this._chatDuidToDraftAccess.delete(chatDuid);
  },
};

export { actions, getters };
